import { Lightning, Log } from '@lightningjs/sdk'
import { ContentPlayer } from '../../../../lib/components/ft-streaming-player/content-player'
import { ItemNode } from '../ItemNode'
import ExpandableTileLandscape from './ExpandableTileLandscape'

/**
 * MidCarouselTile
 *
 * A dedicated tile component for mid-carousel rows (row_type === 'carousel').
 * Extends ExpandableTileLandscape and adds the specific logic required for
 * synchronized in-row video autoplay:
 *
 *  - This tile relies entirely on `autoplay.ts` to enforce the delay timer and content
 *    restrictions. `MidCarouselTile` itself has no internal timer for the video.
 *  - The parent grid (e.g. `MainGrid`) listens to 'onGridAutoplay' and directly invokes
 *    `_startVideoPreview` on the active tile dynamically.
 *  - Once invoked, this class dynamically punches a hole through the UI layer using
 *    `Lightning.shaders.Hole`, allowing the native video player behind the UI to be visible.
 *  - Cleans up the hole effect on unfocus or detach.
 *
 * Usage: Set as the component `type` when rendering items for row_type === 'carousel'.
 */
export default class MidCarouselTile extends ExpandableTileLandscape {

    _videoItem: ItemNode | null = null
    private _isPlaying: boolean = false
    private static _activePreview: MidCarouselTile | null = null

    static override _template() {
        return super._template()
    }

    set videoItem(item: ItemNode | null) {
        this._videoItem = item
    }

    get videoItem(): ItemNode | null {
        return this._videoItem
    }

    override _focus() {
        super._focus()
        if (MidCarouselTile._activePreview && MidCarouselTile._activePreview !== this) {
            MidCarouselTile._activePreview._stopVideoPreview()
        }

        MidCarouselTile._activePreview = this
        Log.info('MidCarouselTile: focused')
    }

    override _unfocus() {
        super._unfocus()

        if (MidCarouselTile._activePreview === this) {
            this._stopVideoPreview()
            MidCarouselTile._activePreview = null
        }
    }

    override _detach() {
        if (MidCarouselTile._activePreview === this) {
            this._stopVideoPreview()
            MidCarouselTile._activePreview = null
        }

        super._detach()
    }

    _startVideoPreview(): void {
        if (!this._videoItem) {
            Log.warn('MidCarouselTile: _startVideoPreview called but no videoItem set')
            return
        }

        Log.info('MidCarouselTile: Starting video preview', { nodeId: this._videoItem.node_id })

        const wrapper = this.tag('Wrapper')
        if (!wrapper) return

        try {
            const contentPlayer = ContentPlayer.getInstance()
            const videoElement = contentPlayer.getVideoEl()

            if (!videoElement) {
                Log.error('MidCarouselTile: Shared video element not available')
                return
            }

            const artwork = wrapper.tag('Artwork')
            if (artwork) {
                artwork.visible = true
                artwork.shader = {
                    type: Lightning.shaders.Hole,
                    x: 0,
                    y: 0,
                    w: artwork.w,
                    h: artwork.h,
                    // radius: 10
                }
            }

            const coords = artwork ? artwork.core.getAbsoluteCoords(0, 0) : [0, 0];
            ;(this as any).fireAncestors('$punchHole', {
                x: coords[0],
                y: coords[1],
                w: artwork ? artwork.w : this.w,
                h: artwork ? artwork.h : this.h,
            })

            this._isPlaying = true

        } catch (error: any) {
            Log.error('MidCarouselTile: Failed to start direct video texture', error)
            this._stopVideoPreview()
        }
    }

    private _stopVideoPreview(): void {
        if (!this._isPlaying) {
            return
        }

        Log.info('MidCarouselTile: Stopping video preview')

        const wrapper = this.tag('Wrapper')
        if (!wrapper) return

        const artwork = wrapper.tag('Artwork')
        if (artwork) {
            artwork.shader = null
            artwork.visible = true
        }

        this.fireAncestors('$removeHole')

        this._isPlaying = false
    }
}
